import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useNavigate } from 'react-router-dom';
import { attendanceAPI } from '../services/api';
import { Position, Worker } from '../types';
import { showCustomAlert } from './CustomAlert';

const monthOptions = [
  { label: 'Januar', value: 0 },
  { label: 'Februar', value: 1 },
  { label: 'Mart', value: 2 },
  { label: 'April', value: 3 },
  { label: 'Maj', value: 4 },
  { label: 'Jun', value: 5 },
  { label: 'Jul', value: 6 },
  { label: 'Avgust', value: 7 },
  { label: 'Septembar', value: 8 },
  { label: 'Oktobar', value: 9 },
  { label: 'Novembar', value: 10 },
  { label: 'Decembar', value: 11 }
];

function formatDateSerbian(dateInput?: string | Date | null): string {
  if (!dateInput) return '—';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}. ${mm}. ${yyyy}.`;
}

const Workers: React.FC = () => {
  const navigate = useNavigate();

  const [workers, setWorkers] = useState<Worker[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    employeeId: string;
    positionId: string | null;
    dateOfEmployment: Date | null;
    annualLeaveDaysNew: number;
    slavaName: string;
    slavaMonth: number | null;
    slavaDay: number | null;
  }>({
    firstName: '',
    lastName: '',
    employeeId: '',
    positionId: null,
    dateOfEmployment: new Date(),
    annualLeaveDaysNew: 20,
    slavaName: '',
    slavaMonth: null,
    slavaDay: null
  });

  const positionOptions = useMemo(
    () =>
      positions.map((p) => ({
        label: p.name,
        value: p._id
      })),
    [positions]
  );

  const fetchPositions = async () => {
    try {
      const data = await attendanceAPI.getPositions({ isActive: true });
      setPositions(data);
    } catch (e) {
      console.error('Error fetching positions:', e);
    }
  };

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const data = await attendanceAPI.getWorkers({ isActive: showAll ? null : true });
      setWorkers(data);
    } catch (e: any) {
      console.error('Error fetching workers:', e);
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju radnika');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  useEffect(() => {
    fetchWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const openCreate = () => {
    setEditingWorker(null);
    setError('');
    setFormData({
      firstName: '',
      lastName: '',
      employeeId: '',
      positionId: null,
      dateOfEmployment: new Date(),
      annualLeaveDaysNew: 20,
      slavaName: '',
      slavaMonth: null,
      slavaDay: null
    });
    setModalVisible(true);
  };

  const openEdit = (w: Worker) => {
    setEditingWorker(w);
    setError('');
    setFormData({
      firstName: w.firstName || '',
      lastName: w.lastName || '',
      employeeId: w.employeeId || '',
      positionId: typeof w.positionId === 'string' ? w.positionId : w.positionId?._id || null,
      dateOfEmployment: w.dateOfEmployment ? new Date(w.dateOfEmployment) : new Date(),
      annualLeaveDaysNew: w.annualLeaveDaysNew ?? 20,
      slavaName: w.slavaName || '',
      slavaMonth: w.slavaMonth ?? null,
      slavaDay: w.slavaDay ?? null
    });
    setModalVisible(true);
  };

  const submitWorker = async () => {
    try {
      setError('');
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('Ime i prezime su obavezni.');
        return;
      }
      if (!formData.positionId) {
        setError('Pozicija je obavezna.');
        return;
      }
      if (!formData.dateOfEmployment) {
        setError('Datum zaposlenja je obavezan.');
        return;
      }

      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        employeeId: formData.employeeId.trim() || undefined,
        positionId: formData.positionId,
        dateOfEmployment: formData.dateOfEmployment.toISOString(),
        annualLeaveDaysNew: formData.annualLeaveDaysNew,
        slavaName: formData.slavaName.trim() || undefined,
        slavaMonth: formData.slavaMonth ?? undefined,
        slavaDay: formData.slavaDay ?? undefined
      };

      if (editingWorker) {
        await attendanceAPI.updateWorker(editingWorker._id, {
          ...payload,
          employeeId: payload.employeeId ?? null,
          slavaName: payload.slavaName ?? null,
          slavaMonth: payload.slavaMonth ?? null,
          slavaDay: payload.slavaDay ?? null
        });
        showCustomAlert.success('Radnik je ažuriran');
      } else {
        await attendanceAPI.createWorker(payload);
        showCustomAlert.success('Radnik je dodat');
      }

      setModalVisible(false);
      fetchWorkers();
    } catch (e: any) {
      console.error('Error saving worker:', e);
      setError(e?.response?.data?.message || 'Greška pri čuvanju radnika');
    }
  };

  const deactivateWorker = (w: Worker) => {
    confirmDialog({
      header: 'Potvrda',
      icon: 'pi pi-exclamation-triangle',
      message: `Da li ste sigurni da želite da deaktivirate radnika ${w.lastName} ${w.firstName}?`,
      acceptLabel: 'Deaktiviraj',
      rejectLabel: 'Otkaži',
      accept: async () => {
        try {
          await attendanceAPI.deactivateWorker(w._id);
          showCustomAlert.success('Radnik je deaktiviran');
          fetchWorkers();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri deaktivaciji radnika');
        }
      }
    });
  };

  const deletePermanent = (w: Worker) => {
    confirmDialog({
      header: 'Trajno brisanje',
      icon: 'pi pi-exclamation-triangle',
      message: `Da li ste sigurni da želite da trajno obrišete radnika ${w.lastName} ${w.firstName}? Ova akcija se ne može poništiti.`,
      acceptLabel: 'Obriši trajno',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await attendanceAPI.deleteWorkerPermanent(w._id);
          showCustomAlert.success('Radnik je trajno obrisan');
          fetchWorkers();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri trajnom brisanju radnika');
        }
      }
    });
  };

  const statusTemplate = (w: Worker) => {
    const label = w.isActive ? 'Aktivan' : 'Neaktivan';
    const cls = w.isActive ? 'bg-green-500' : 'bg-gray-500';
    return (
      <span className={`inline-flex align-items-center gap-2 px-3 py-1 text-white text-sm border-round ${cls}`}>
        <i className={`pi ${w.isActive ? 'pi-check' : 'pi-times'}`} />
        {label}
      </span>
    );
  };

  const positionTemplate = (w: Worker) => {
    if (!w.positionId) return '—';
    if (typeof w.positionId === 'string') return '—';
    return w.positionId.name;
  };

  const actionsTemplate = (w: Worker) => {
    return (
      <div className="flex gap-2 justify-content-end">
        <Button
          icon="pi pi-eye"
          rounded
          severity="secondary"
          className="p-button-sm"
          tooltip="Detalji"
          tooltipOptions={{ position: 'left' }}
          onClick={() => navigate(`/workers/${w._id}`)}
        />
        <Button
          icon="pi pi-pencil"
          rounded
          severity="warning"
          className="p-button-sm"
          tooltip="Izmeni"
          tooltipOptions={{ position: 'left' }}
          onClick={() => openEdit(w)}
        />
        <Button
          icon="pi pi-ban"
          rounded
          severity="warning"
          className="p-button-sm"
          tooltip="Deaktiviraj"
          tooltipOptions={{ position: 'left' }}
          onClick={() => deactivateWorker(w)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          severity="danger"
          className="p-button-sm"
          tooltip="Obriši trajno"
          tooltipOptions={{ position: 'left' }}
          onClick={() => deletePermanent(w)}
        />
      </div>
    );
  };

  return (
    <div className="p-4">
      <Card>
        <div className="flex justify-content-between align-items-center mb-3">
          <h2 className="m-0">Upravljanje radnicima</h2>
        </div>

        <div className="flex justify-content-between align-items-center mb-4 p-3 border-1 border-round surface-border bg-white">
          <div className="flex gap-2">
            <Button
              label="Dodaj radnika"
              icon="pi pi-plus"
              onClick={openCreate}
              severity="success"
            />
            <Button
              label={showAll ? 'Prikaži aktivne' : 'Prikaži sve'}
              icon="pi pi-eye"
              onClick={() => setShowAll((v) => !v)}
              className="p-button-outlined"
            />
          </div>
          <Button
            label="Osveži"
            icon="pi pi-refresh"
            onClick={fetchWorkers}
            severity="secondary"
          />
        </div>

        <DataTable
          value={workers}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          currentPageReportTemplate="Prikaz {first} do {last} od ukupno {totalRecords} radnika"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          emptyMessage="Nema radnika"
          sortMode="multiple"
          className="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column field="employeeId" header="ID" sortable style={{ width: '110px' }} body={(w: Worker) => w.employeeId || '—'} />
          <Column field="lastName" header="Prezime" sortable />
          <Column field="firstName" header="Ime" sortable />
          <Column header="Pozicija" body={positionTemplate} sortable sortField="positionId.name" />
          <Column
            field="dateOfEmployment"
            header="Datum zaposlenja"
            sortable
            body={(w: Worker) => formatDateSerbian(w.dateOfEmployment)}
            style={{ width: '180px' }}
          />
          <Column header="Status" body={statusTemplate} style={{ width: '160px' }} />
          <Column header="" body={actionsTemplate} style={{ width: '220px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editingWorker ? 'Izmeni radnika' : 'Novi radnik'}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '560px' }}
        contentStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        draggable={false}
        className="p-fluid"
      >
        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="field">
          <label className="block mb-2">
            Ime <span className="text-red-500">*</span>
          </label>
          <InputText value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
        </div>

        <div className="field">
          <label className="block mb-2">
            Prezime <span className="text-red-500">*</span>
          </label>
          <InputText value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
        </div>

        <div className="field">
          <label className="block mb-2">ID radnika (opciono)</label>
          <InputText
            value={formData.employeeId}
            placeholder="Unesite ID radnika ako je potrebno"
            onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="block mb-2">
            Pozicija <span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={formData.positionId}
            options={positionOptions}
            placeholder="Izaberite poziciju"
            onChange={(e) => setFormData({ ...formData, positionId: e.value })}
            showClear
            appendTo="self"
          />
          <small className="text-600">Pozicije možete dodati u sekciji "Pozicije"</small>
        </div>

        <div className="field">
          <label className="block mb-2">
            Datum zaposlenja <span className="text-red-500">*</span>
          </label>
          <Calendar
            value={formData.dateOfEmployment}
            onChange={(e) => setFormData({ ...formData, dateOfEmployment: e.value as Date })}
            dateFormat="dd.mm.yy"
            showIcon
          />
        </div>

        <div className="field">
          <label className="block mb-2">
            Dani novog godišnjeg odmora <span className="text-red-500">*</span>
          </label>
          <InputNumber
            value={formData.annualLeaveDaysNew}
            onValueChange={(e) => setFormData({ ...formData, annualLeaveDaysNew: e.value ?? 20 })}
            showButtons
            buttonLayout="vertical"
            min={0}
          />
          <small className="text-600">Podrazumevano: 20 dana godišnjeg odmora</small>
        </div>

        <div className="field">
          <label className="block mb-2">Naziv slave (opciono)</label>
          <InputText
            value={formData.slavaName}
            placeholder="Npr. Sv. Nikola"
            onChange={(e) => setFormData({ ...formData, slavaName: e.target.value })}
          />
          <small className="text-600">Naziv slave koja se slavi svake godine</small>
        </div>

        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Mesec slave (opciono)</label>
              <Dropdown
                value={formData.slavaMonth}
                options={monthOptions}
                placeholder="Izaberite mesec"
                onChange={(e) => setFormData({ ...formData, slavaMonth: e.value })}
                showClear
                appendTo="self"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Dan slave (opciono)</label>
              <InputNumber
                value={formData.slavaDay}
                onValueChange={(e) => setFormData({ ...formData, slavaDay: e.value ?? null })}
                placeholder="Dan (1-31)"
                min={1}
                max={31}
              />
            </div>
          </div>
        </div>

        <small className="text-600 block mb-3">
          Slava traje samo jedan dan i ponavlja se svake godine na isti dan i mesec
        </small>

        <div className="flex justify-content-end gap-2">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setModalVisible(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" severity="info" onClick={submitWorker} />
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default Workers;


